import { ConfirmationDialog } from 'components/ConfirmationDialog';
import type { IDropdownContextProps } from 'components/DropdownContext';
import { DropdownContext } from 'components/DropdownContext';
import { ErrorHandlingModal } from 'components/ErrorHandlingModal';
import { ModalLoading } from 'components/ModalLoading';
import { RequestStatus } from 'features/request/requestSlice';
import type { ContractStatusEnum, ProjectData, ProjectStatusEnum, WorkOrderStatusEnum } from 'gql/graphql';
import { ProjectMethodologyEnum, WorkOrderChangeStatusEnum } from 'gql/graphql';
import { useMethodsWorkOrder } from 'hooks/useMethodsWorkOrder';
import { useUerExportWorkOrderPrices } from 'hooks/useUerExportWorkOrderPrices';
import { useWorkOrderChangeCreate } from 'hooks/useWorkOrderChangeCreate';
import { useWorkOrderDownload } from 'hooks/useWorkOrderDownload';

import * as R from 'ramda';
import { useCallback, useMemo, useState } from 'react';

import { CreateClosingWorkOrderChange } from '../../WorkOrderChange/CreateClosingWorkOrderChange/CreateClosingWorkOrderChange';
import { WorkOrderChangeCreateOrEdit } from '../../WorkOrderChange/WorkOrderChangeCreateOrEdit';
import { ExportActKC2TemplateModal } from '../ExportActKC2TemplateModal/ExportActKC2TemplateModal';
import { ImportActKC2TemplateModal } from '../ImportActKC2TemplateModal/ImportActKC2TemplateModal';
import { RollbackWorkOrderChange } from '../RollbackWorkOrderChange';

import { useAddItemsWorkOrderKebabMenu } from './useAddItemsWorkOrderKebabMenu';
import { WorkOrderKebabMenuTooltip } from './WorkOrderKebabMenuTooltip';

interface IWorkOrderToolbar extends Partial<IDropdownContextProps> {
    workOrderId: number;
    statusContract?: ContractStatusEnum;
    statusProject: ProjectStatusEnum | undefined;
    statusWorkOrder?: WorkOrderStatusEnum;
    projectData: ProjectData | undefined | null;
    workOrderLabel: string;
    contractId: number;
    isNotActKC2: boolean;
    isWorkOrderVorTypeXml: boolean;
    refreshProjectData: () => void;
    canRollbackWorkOrderChange: boolean;
    isDisabledByProjectStatus?: boolean;
    refreshWorkOrder: () => void;
    refreshWorkOrderActs?: () => void;
    refreshVersionsData: () => void;
}

export const WorkOrderKebabMenu = ({
    workOrderId,
    statusContract,
    statusProject,
    projectData,
    workOrderLabel,
    contractId,
    statusWorkOrder,
    isNotActKC2,
    isWorkOrderVorTypeXml,
    isDisabledByProjectStatus,
    refreshProjectData,
    refreshWorkOrder,
    refreshWorkOrderActs,
    refreshVersionsData,
    canRollbackWorkOrderChange,
    ...propsDropdown
}: IWorkOrderToolbar) => {
    const [isExportActKC2TemplateOpen, setIsExportActKC2TemplateOpen] = useState<boolean>(false);
    const [isImportActKC2TemplateOpen, setIsImportActKC2TemplateOpen] = useState<boolean>(false);
    const [isRollbackWorkOrderOpen, setIsRollbackWorkOrderOpen] = useState<boolean>(false);
    const [isCreateClosingWorkOrderChangeOpen, setIsCreateClosingWorkOrderChangeOpen] = useState<boolean>(false);

    const { closeCreatingWorkOrderChangeHandler, openCreatingWorkOrderChangeHandler, isCreateWorkOrderChangeOpened } =
        useWorkOrderChangeCreate({ workOrderId });

    const switchIsCreateClosingWorkOrderChangeOpen = useCallback(
        () => setIsCreateClosingWorkOrderChangeOpen(R.not),
        [],
    );

    const {
        deleteData,
        deleteStatus,
        deleteError,
        isSubmitted,
        isDialogOpen,
        handleDeleteButton,
        handleDialogClose,
        handleDeleteWorkOrder,
        handleCloseModalError,
        handleCreateReport,
    } = useMethodsWorkOrder(workOrderId);

    const {
        isSubmittedDownload,
        workOrderAsExcelData,
        workOrderAsExcelStatus,
        workOrderAsExcelError,
        workOrderAsExcelAbort,
        handleDownloadWorkOrder,
        handleCloseModalErrorDownload,
        handleCreateReportDownload,
    } = useWorkOrderDownload({
        workOrderId,
        instanceId: 'fromPage',
        workOrderStatus: statusWorkOrder,
        methodology: ProjectMethodologyEnum.Uer,
    });

    const { doUerExportWorkOrderPricesRequest, doUerExportWorkOrderPricesAbort, uerExportWorkOrderPricesStatus } =
        useUerExportWorkOrderPrices({ workOrderId });

    const isWorkOrderLoading = useMemo(
        () => workOrderAsExcelStatus === RequestStatus.FETCHING,
        [workOrderAsExcelStatus],
    );
    const isWorkOrderPriceLoading = useMemo(
        () => uerExportWorkOrderPricesStatus === RequestStatus.FETCHING,
        [uerExportWorkOrderPricesStatus],
    );

    const cancelLoadingExcel = useCallback(() => workOrderAsExcelAbort(), [workOrderAsExcelAbort]);

    const onExportActKc2TemplateClose = useCallback(() => setIsExportActKC2TemplateOpen(false), []);

    const onImportActKc2TemplateClose = useCallback(
        (isUploaded?: boolean) => {
            setIsImportActKC2TemplateOpen(false);

            if (isUploaded) {
                refreshWorkOrder();
                refreshWorkOrderActs?.();
                refreshProjectData();
            }
        },
        [refreshProjectData, refreshWorkOrder, refreshWorkOrderActs],
    );

    const exportActKC2TemplateOpen = useCallback(() => {
        setIsExportActKC2TemplateOpen(true);
    }, []);

    const importActKC2TemplateOpen = useCallback(() => {
        setIsImportActKC2TemplateOpen(true);
    }, []);

    const toggleRollbackWorkOrder = useCallback(() => {
        setIsRollbackWorkOrderOpen((state) => !state);
    }, []);

    const isHasOrderChangeValidStatus = useMemo(() => {
        const currentWorkOrderNav = projectData?.contractNavigation?.data?.workOrderNavs?.find(
            (nav) => nav.id === workOrderId,
        );

        return !!currentWorkOrderNav?.workOrderChanges?.some(
            (item) =>
                item.status === WorkOrderChangeStatusEnum.OnFormation ||
                item.status === WorkOrderChangeStatusEnum.Approving,
        );
    }, [projectData?.contractNavigation?.data?.workOrderNavs, workOrderId]);

    const {
        workOrderKebabMenuGroups,
        workOrderKebabMenuItems,
        getMenuItemAttributes,
        activeTooltip,
        tooltipPosition,
        onMenuMouseMove,
        onMenuMouseLeave,
    } = useAddItemsWorkOrderKebabMenu({
        projectId: projectData?.uniqueId,
        workOrderId,
        statusContract,
        statusWorkOrder,
        isDisabledByProjectStatus,
        isNotActKC2,
        isWorkOrderVorTypeXml,
        handleDownloadWorkOrder,
        handleDownloadWorkOrderPrices: doUerExportWorkOrderPricesRequest,
        openCreatingWorkOrderChangeHandler,
        openRollbackWorkOrderHandler: toggleRollbackWorkOrder,
        handleDeleteButton,
        exportActKC2TemplateOpen,
        importActKC2TemplateOpen,
        canRollbackWorkOrderChange,
        isHasOrderChangeValidStatus,
        openCreateClosingWorkOrderChangeHandler: switchIsCreateClosingWorkOrderChangeOpen,
    });

    return (
        <>
            <DropdownContext
                items={workOrderKebabMenuItems}
                {...propsDropdown}
                groups={workOrderKebabMenuGroups}
                getItemAttributesHandler={getMenuItemAttributes}
                btnView="ghost"
                btnSize="s"
                btnIconSize="s"
                onMouseMoveHandler={onMenuMouseMove}
                onMouseLeaveHandler={onMenuMouseLeave}
            />
            {isCreateClosingWorkOrderChangeOpen && (
                <CreateClosingWorkOrderChange
                    workOrderId={workOrderId}
                    onClose={switchIsCreateClosingWorkOrderChangeOpen}
                    refreshProjectData={refreshProjectData}
                />
            )}
            {isCreateWorkOrderChangeOpened && (
                <WorkOrderChangeCreateOrEdit
                    contractId={contractId}
                    workOrderId={workOrderId}
                    withButton={false}
                    projectData={projectData}
                    workOrderLabel={workOrderLabel}
                    isCreating
                    isOpenedOuter={isCreateWorkOrderChangeOpened}
                    setIsOpenOuter={closeCreatingWorkOrderChangeHandler}
                    refreshProjectData={refreshProjectData}
                    statusContract={statusContract}
                    statusProject={statusProject}
                />
            )}
            {isExportActKC2TemplateOpen && (
                <ExportActKC2TemplateModal onClose={onExportActKc2TemplateClose} workOrderId={workOrderId} />
            )}
            {isImportActKC2TemplateOpen && (
                <ImportActKC2TemplateModal onClose={onImportActKc2TemplateClose} workOrderId={workOrderId} />
            )}
            {isRollbackWorkOrderOpen && (
                <RollbackWorkOrderChange
                    projectData={projectData}
                    refreshProjectData={refreshProjectData}
                    refreshWorkOrder={refreshWorkOrder}
                    onClose={toggleRollbackWorkOrder}
                    workOrderId={workOrderId}
                    refreshVersionsData={refreshVersionsData}
                />
            )}
            <WorkOrderKebabMenuTooltip
                activeTooltip={activeTooltip}
                tooltipPosition={tooltipPosition}
                statusWorkOrder={statusWorkOrder}
                isWorkOrderVorTypeXml={isWorkOrderVorTypeXml}
                isHasOrderChangeValidStatus={isHasOrderChangeValidStatus}
            />
            <ConfirmationDialog
                textHeader="УДАЛЕНИЕ НАРЯД-ЗАКАЗА"
                textConfirmation="Вы уверены, что хотите удалить текущий наряд-заказ?"
                cancelButtonText="Не удалять"
                confirmButtonText="Удалить"
                isOpened={isDialogOpen}
                onSubmit={handleDeleteWorkOrder}
                onClose={handleDialogClose}
            />
            <ErrorHandlingModal
                titleText="Удаление наряд-заказа"
                isSubmitted={isSubmitted}
                statusRequest={deleteStatus}
                dataRequest={deleteData?.uerDeleteWorkOrder}
                errorsRequest={deleteError}
                handleCloseModalError={handleCloseModalError}
                handleCreateReport={handleCreateReport}
            />
            <ErrorHandlingModal
                titleText="Выгрузка наряд-заказа"
                isSubmitted={isSubmittedDownload}
                statusRequest={workOrderAsExcelStatus}
                dataRequest={workOrderAsExcelData}
                errorsRequest={workOrderAsExcelError}
                handleCloseModalError={handleCloseModalErrorDownload}
                handleCreateReport={handleCreateReportDownload}
            />
            {isWorkOrderLoading && <ModalLoading title="Выгрузка Наряд-заказа" onClose={cancelLoadingExcel} />}
            {isWorkOrderPriceLoading && (
                <ModalLoading title="Выгрузка накопительной ведомости" onClose={doUerExportWorkOrderPricesAbort} />
            )}
        </>
    );
};

WorkOrderKebabMenu.displayName = 'WorkOrderKebabMenu';
